import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Button } from "./Button";
import { Modal } from "./Modal";
import "./Field.css";
import "./ConfirmDialog.css";

export interface ConfirmDialogProps {
  open: boolean;
  /** Name the count and the thing: "Delete 40 mappings?" — never "Are you sure?". */
  title: ReactNode;
  message?: ReactNode;
  /** Button text; also name the count: "Delete 40 mappings". Default "Confirm". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Danger styling + initial focus on Cancel. */
  destructive?: boolean;
  /** May return a promise: the confirm button shows loading until it settles.
   * Resolve → dialog stays controlled by the parent (usually closes it).
   * Reject → loading stops, dialog stays open (show the error via a toast). */
  onConfirm: () => void | Promise<unknown>;
  onCancel: () => void;
  /** If set, the user must type this exact text to enable confirm (big bulk deletes). */
  confirmText?: string;
}

/** Confirmation as role="alertdialog". Esc / Cancel call onCancel. */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel,
  confirmText,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);
  const auto = useId();

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setTyped("");
      setBusy(false);
    }
  }, [open]);

  const blocked = confirmText !== undefined && typed !== confirmText;

  const handleConfirm = async () => {
    if (busy || blocked) return;
    const result = onConfirm();
    if (!result || typeof (result as Promise<unknown>).then !== "function") return;
    setBusy(true);
    try {
      await result;
    } catch {
      // The caller reports the error; keep the dialog open for a retry.
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const initialFocusRef = confirmText !== undefined ? inputRef : destructive ? cancelRef : confirmRef;

  return (
    <Modal
      open={open}
      onClose={busy ? () => undefined : onCancel}
      title={title}
      size="sm"
      role="alertdialog"
      hideClose
      closeOnBackdrop={!destructive && !busy}
      initialFocusRef={initialFocusRef}
      footer={
        <>
          <Button ref={cancelRef} variant="ghost" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={destructive ? "danger" : "primary"}
            loading={busy}
            disabled={blocked}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message || confirmText !== undefined ? (
        <div className="ui-confirm">
          {message ? <div className="ui-confirm__message">{message}</div> : null}
          {confirmText !== undefined ? (
            <div className="ui-confirm__type">
              <label htmlFor={`confirm${auto}`} className="ui-confirm__type-label">
                Type <code>{confirmText}</code> to confirm
              </label>
              <input
                ref={inputRef}
                id={`confirm${auto}`}
                className="ui-control ui-control--md"
                value={typed}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void handleConfirm();
                }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
